import React, { isValidElement, ReactElement, ReactNode } from "react";

type DuplicateStrategy = "last" | "first" | "error";

export interface CollectByDisplayNameOptions {
  /** Which prop to use as the key in the resulting map (default: "value") */
  valueProp?: string;
  /** What to do when two children share the same value key (default: "last") */
  onDuplicate?: DuplicateStrategy;
}

function getElementDisplayName(el: ReactElement): string | undefined {
  const t: any = el.type;
  if (typeof t === "string") return undefined; // intrinsic elements like "div"
  return t.displayName ?? t.name ?? t?.type?.displayName ?? t?.type?.name;
}

function collectText(node: ReactNode): string[] {
  const out: string[] = [];

  function walk(n: ReactNode): void {
    if (n == null || typeof n === "boolean") return;

    if (typeof n === "string" || typeof n === "number") {
      out.push(String(n));
      return;
    }

    if (Array.isArray(n)) {
      for (const item of n) walk(item);
      return;
    }

    if (isValidElement(n)) {
      // Recurse into element's children (works for fragments too)
      const inner = (n.props as any)?.children;
      if (inner != null) walk(inner);
      return;
    }

    // Other node-like things (e.g., iterables)
    if (Symbol.iterator in Object(n) as any) {
      for (const item of n as Iterable<ReactNode>) walk(item);
    }
  }

  walk(node);
  return out;
}

/**
 * Recursively traverse ALL children, but only collect those whose component
 * displayName is in `names`. The result is an object keyed by `props[valueProp]`,
 * where each entry contains the element and an array of descendant text nodes.
 */
export function collectChildrenByDisplayName(
    children: ReactNode,
    names: string | string[],
    opts: CollectByDisplayNameOptions = {}
): Record<string, { element: ReactElement; text: string[] }> {
  const nameSet = new Set(Array.isArray(names) ? names : [names]);
  const { valueProp = "value", onDuplicate = "last" } = opts;

  const out: Record<
      string,
      { element: ReactElement; text: string[] }
  > = Object.create(null);

  function add(key: string, el: ReactElement) {
    if (key in out) {
      if (onDuplicate === "error") {
        throw new Error(
            `Duplicate key "${key}" while collecting children by displayName.`
        );
      }
      if (onDuplicate === "first") return; // keep existing
      // "last" overwrites
    }

    const text = collectText((el.props as any)?.children);
    out[key] = { element: el, text };
  }

  function walk(node: ReactNode): void {
    React.Children.forEach(node, (child) => {
      if (!isValidElement(child)) {
        // still need to dive into arrays/fragments outside of valid elements
        if (Array.isArray(child)) walk(child);
        return;
      }

      // If it matches, collect it
      const displayName = getElementDisplayName(child);
      if (displayName && nameSet.has(displayName)) {
        const keyVal = (child.props as any)?.[valueProp];
        if (keyVal != null) add(String(keyVal), child);
        // If the value prop is missing/null, we simply skip collecting it
      }

      // Always traverse deeper, regardless of match
      const inner = (child.props as any)?.children;
      if (inner != null) walk(inner);
    });
  }

  walk(children);
  return out;
}
